import React from "react";
import { Eye, MessagesSquare, Sparkles } from "lucide-react";
import { Localizer } from "@/main/react/Types";
import { readGroupActivity } from "@/main/common/GroupActivityTypes";

/**
 * Honest community size (design `designs/01-now/2026-09-10-honest-community-size-design.md`): what a
 * group card, row or header may say about a group's size, in place of its member count.
 *
 * Three outcomes, all decided on the server (`GroupActivityPolicy`): the weekly activity line (each half
 * only when nonzero), "New group", or NOTHING. There is deliberately no fallback to the member count and
 * no "0 visitors": below the floor a quiet group shows nothing numeric (C3), because an absence is not
 * rendered as a failure.
 *
 * Each half is its own item with its own complete key ("12 visitors this week", "7 posts and replies
 * this week"): §7 gives each half a whole sentence, and composing one translated fragment with another
 * is not allowed. Every item carries `data-group-activity` — the stable hook the anonymous-lane spec and
 * the SSR twin (`group.jte`) share.
 */
export type GroupActivityItemKey = "visitors" | "contributions" | "new";

export type GroupActivityItem = {
    key: GroupActivityItemKey;
    label: string;
};

/** One icon per item, for badge rows that pair an icon with each label (the group card). */
export const GROUP_ACTIVITY_ICONS: Record<GroupActivityItemKey, React.ReactNode> = {
    visitors: <Eye className="size-3.5" aria-hidden={true}/>,
    contributions: <MessagesSquare className="size-3.5" aria-hidden={true}/>,
    new: <Sparkles className="size-3.5" aria-hidden={true}/>,
};

export function getGroupActivityItems(localizer: Localizer, group: object | null | undefined): GroupActivityItem[] {
    const activity = readGroupActivity(group);
    if (activity.kind === "new") {
        return [{ key: "new", label: localizer("group.activity.new") }];
    }
    if (activity.kind !== "activity") {
        return [];
    }
    const items: GroupActivityItem[] = [];
    if (activity.visitorsThisWeek != null) {
        items.push({ key: "visitors", label: localizer("group.activity.visitors", String(activity.visitorsThisWeek)) });
    }
    if (activity.contributionsThisWeek != null) {
        items.push({ key: "contributions", label: localizer("group.activity.contributions", String(activity.contributionsThisWeek)) });
    }
    return items;
}

/**
 * The inline form, for text metadata rows (search results, rails, the neighbourhood card, the welcome
 * overlay): one `<span>` per item, rendering nothing at all when the server decided nothing.
 */
export function GroupActivityInline({
    group,
    localizer,
    itemClassName,
}: {
    group: object | null | undefined;
    localizer: Localizer;
    itemClassName?: string;
}) {
    const items = getGroupActivityItems(localizer, group);
    if (items.length === 0) {
        return null;
    }
    return (
        <>
            {items.map((item) => (
                <span key={item.key} className={itemClassName} data-group-activity={item.key}>
                    {item.label}
                </span>
            ))}
        </>
    );
}

export default GroupActivityInline;
